import type { ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, requestWithoutContent, withQuery } from "../http/httpClient";
import { readArray, readInteger, readObject, readString } from "../http/jsonDecoding";
import {
  decodeAccessList,
  decodeAccessPerson,
  decodePersonPanel,
  decodeStarterGroups,
  decodeSuggestions,
  grantBody,
  type AccessList,
  type AccessPerson,
  type AccountSuggestion,
  type Grant,
  type GrantOverride,
  type PersonPanel,
  type StarterGroup,
} from "./accessModel";

/**
 * The routes of groups as permission sets (accounts.md GS-1 to GS-6). A scope is the
 * installation (`null`) or one project; a group is one resource whatever its scope. Every
 * change is checked again by the server, which refuses what the viewer may not do.
 */

const GROUPS_PATH = "/accounts/groups";

/** The groups every new project starts with, and what each allows (GS-5): fixed, so only read. */
export function readStarterGroups(signal?: AbortSignal): Promise<readonly StarterGroup[]> {
  return requestJson({ method: "GET", path: "/accounts/starter-groups", signal, decodeResponse: decodeStarterGroups });
}

/** The scope's groups, first on top, with what each gives and whom it reaches. */
export function readAccessList(projectId: string | null, signal?: AbortSignal): Promise<AccessList> {
  return requestJson({ method: "GET", path: projectId === null ? GROUPS_PATH : `${projectPath(projectId)}/access`, signal, decodeResponse: decodeAccessList });
}

/**
 * Everyone the project's groups reach, and its item curators, with their names: whom a
 * Run's authors, hosts and mentions are. Anyone who reads the project reads them.
 */
export async function listProjectPeople(projectId: string, signal?: AbortSignal): Promise<readonly AccessPerson[]> {
  const list = await readAccessList(projectId, signal);
  return list.people.flatMap((person) => {
    const known = list.namedPeople.get(person.userId);
    return known === undefined ? [] : [known];
  });
}

/**
 * The active people the project's list reaches who may review one item now: Curate for its
 * kind through a group, with View, or curating the item itself (accounts.md GS-2, GS-6).
 */
export function listItemReviewers(projectId: string, item: { readonly kind: string; readonly itemId: string }, signal?: AbortSignal): Promise<readonly AccessPerson[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`${projectPath(projectId)}/reviewers`, { item_kind: item.kind, item_id: item.itemId }),
    signal,
    decodeResponse: (body) => readArray(body, "reviewers", (person) => decodeAccessPerson(readObject(person, "reviewer"))),
  });
}

/**
 * Accounts not in the group yet that match what was typed, for those who manage it (F15).
 * The server decides how much typing is enough; until then it suggests nobody.
 */
export function suggestGroupMembers(groupId: string, query: string, signal?: AbortSignal): Promise<readonly AccountSuggestion[]> {
  const body = { query } satisfies ApiSchema<"MemberSuggestionQueryBody">;
  return requestJson({ method: "POST", path: `${groupPath(groupId)}/member-suggestions`, body: jsonBody(body), signal, decodeResponse: decodeSuggestions });
}

/** One person's access in a project: each permission, and what gives it or would. */
export function readPersonPanel(projectId: string, userId: string, signal?: AbortSignal): Promise<PersonPanel> {
  return requestJson({ method: "GET", path: `${projectPath(projectId)}/access/people/${encodeURIComponent(userId)}`, signal, decodeResponse: decodePersonPanel });
}

/** Writes the whole order against the revision it was read at; returns the new revision. */
export function reorderGroups(projectId: string | null, groupIds: readonly string[], revision: number): Promise<number> {
  const body = { group_ids: [...groupIds], revision } satisfies ApiSchema<"GroupOrderBody">;
  return requestJson({
    method: "PUT",
    path: projectId === null ? `${GROUPS_PATH}/order` : `${projectPath(projectId)}/access/order`,
    body: jsonBody(body),
    decodeResponse: (response) => readInteger(readObject(response, "order"), "revision"),
  });
}

/** Includes an installation group in the project, or sets what it switches on or off there. */
export function includeGroup(projectId: string, groupId: string, overrides: readonly GrantOverride[], reason: string | null): Promise<void> {
  const body = {
    overrides: overrides.map((choice) => ({ ...grantBody(choice), override: choice.override })),
    reason,
  } satisfies ApiSchema<"InclusionBody">;
  return requestWithoutContent({ method: "PUT", path: inclusionPath(projectId, groupId), body: jsonBody(body) });
}

export function excludeGroup(projectId: string, groupId: string): Promise<void> {
  return requestWithoutContent({ method: "DELETE", path: inclusionPath(projectId, groupId) });
}

/** Makes a group at the bottom of the scope's list, or inside an installation group. */
export function createGroup(projectId: string | null, name: string, grants: readonly Grant[], parentId: string | null = null): Promise<string> {
  const body = { name, parent_id: parentId, grants: grants.map(grantBody) } satisfies ApiSchema<"GroupCreationBody">;
  return requestJson({
    method: "POST",
    path: projectId === null ? GROUPS_PATH : `${projectPath(projectId)}/groups`,
    body: jsonBody(body),
    decodeResponse: (response) => readString(readObject(response, "group"), "group_id"),
  });
}

/** Renames a group; for an installation group, also moves it into another (`null`: to the top). */
export function changeGroup(groupId: string, name: string, parentId: string | null): Promise<void> {
  const body = { name, parent_id: parentId } satisfies ApiSchema<"GroupChangeBody">;
  return requestWithoutContent({ method: "PUT", path: groupPath(groupId), body: jsonBody(body) });
}

/** Sets the group's whole set; switching on the server's keys needs a reason. */
export function setGroupGrants(groupId: string, grants: readonly Grant[], reason: string | null): Promise<void> {
  const body = { grants: grants.map(grantBody), reason } satisfies ApiSchema<"GroupGrantsBody">;
  return requestWithoutContent({ method: "PUT", path: `${groupPath(groupId)}/grants`, body: jsonBody(body) });
}

/** Removes a group, its members and every project's inclusion of it. */
export function deleteGroup(groupId: string): Promise<void> {
  return requestWithoutContent({ method: "DELETE", path: groupPath(groupId) });
}

export function addGroupMember(groupId: string, userId: string): Promise<void> {
  const body = { user_id: userId } satisfies ApiSchema<"GroupMemberBody">;
  return requestWithoutContent({ method: "POST", path: `${groupPath(groupId)}/members`, body: jsonBody(body) });
}

export function removeGroupMember(groupId: string, userId: string): Promise<void> {
  return requestWithoutContent({ method: "DELETE", path: `${groupPath(groupId)}/members/${encodeURIComponent(userId)}` });
}

function groupPath(groupId: string): `/${string}` {
  return `${GROUPS_PATH}/${encodeURIComponent(groupId)}`;
}

function projectPath(projectId: string): `/${string}` {
  return `/projects/${encodeURIComponent(projectId)}`;
}

function inclusionPath(projectId: string, groupId: string): `/${string}` {
  return `${projectPath(projectId)}/access/included/${encodeURIComponent(groupId)}`;
}
